import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { Loader2 } from 'lucide-react';

// Well-known India states + UTs geojson (~180 KB, 36 features)
const STATES_URL =
  'https://gist.githubusercontent.com/jbrobst/56c13bbbf9d97d187fea01ca62ea5112/raw/e388c4cae20aa53cb5090210a42ebb9b765c0a36/india_states.geojson';

// Map our data-side state name to the GeoJSON `ST_NM` value
const STATE_ALIASES = {
  'Delhi NCR': ['NCT of Delhi', 'Delhi'],
  'Uttar Pradesh': ['Uttar Pradesh'],
  'Andhra Pradesh': ['Andhra Pradesh'],
  'Odisha': ['Odisha', 'Orissa'],
  'Telangana': ['Telangana'],
  'Jammu & Kashmir': ['Jammu & Kashmir', 'Jammu and Kashmir'],
};

const dataNameFor = (feat) => {
  const g = feat?.properties?.ST_NM || feat?.properties?.st_nm || feat?.properties?.NAME_1 || feat?.properties?.name || '';
  const upper = g.trim();
  for (const [ourName, aliases] of Object.entries(STATE_ALIASES)) {
    if (aliases.includes(upper)) return ourName;
  }
  return upper;
};

export default function IndiaChoropleth({ stateReach, onHover, hovered, height = 500 }) {
  const wrapRef = useRef(null);
  const [statesGeo, setStatesGeo] = useState(null);
  const [width, setWidth] = useState(760);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(STATES_URL);
        if (!r.ok) throw new Error('Failed to fetch India states GeoJSON');
        const j = await r.json();
        if (alive) setStatesGeo(j);
      } catch (e) {
        if (alive) setErr(e.message || 'Failed to load map');
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(Math.floor(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const benLookup = useMemo(() => {
    const m = {};
    stateReach.forEach((s) => { m[s.state] = s; });
    return m;
  }, [stateReach]);

  const maxBen = useMemo(() => Math.max(1, ...stateReach.map((s) => s.beneficiaries)), [stateReach]);

  const colorFor = (v) => {
    if (!v) return '#F3F4F6';
    const t = v / maxBen;
    if (t < 0.35) return `rgba(255, 194, 14, ${0.35 + t * 0.85})`;
    return `rgba(226, 59, 51, ${0.35 + t * 0.65})`;
  };

  const { statesPaths } = useMemo(() => {
    if (!statesGeo) return { statesPaths: [] };
    const proj = geoMercator().fitSize([width, height], statesGeo);
    const p = geoPath(proj);
    const sPaths = statesGeo.features.map((f, i) => ({
      feat: f,
      d: p(f) || '',
      key: (f.properties?.ST_NM || i) + '-' + i,
      name: dataNameFor(f),
    }));
    return { statesPaths: sPaths };
  }, [statesGeo, width, height]);

  const total = stateReach.reduce((s, x) => s + x.beneficiaries, 0);

  return (
    <div className="p-5 min-h-[520px]">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--gray-500)]">
            India choropleth · beneficiary density
          </div>
          <div className="text-[11.5px] text-[color:var(--gray-400)]">GeoJSON boundaries · 36 states &amp; Union Territories</div>
        </div>
        <span className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase text-[color:var(--gray-500)]">Fewer</span>
          {[0.3, 0.5, 0.7, 0.9].map((t) => (
            <span key={t} className="h-2.5 w-6" style={{ background: t < 0.4 ? `rgba(255,194,14,${t + 0.15})` : `rgba(226,59,51,${t})` }} />
          ))}
          <span className="text-[10px] uppercase text-[color:var(--gray-500)]">More</span>
        </span>
      </div>

      <div ref={wrapRef} className="relative w-full" style={{ minHeight: height + 20 }} data-testid="india-map-wrap">
        {!statesGeo && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-[color:var(--gray-500)] text-[13px] gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading India boundaries…
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center text-[color:var(--red-600)] text-[13px] px-6 text-center">
            {err}. Please refresh the page — the map fetches its GeoJSON from a public CDN.
          </div>
        )}
        {statesGeo && (
          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} role="img" aria-label="India beneficiary choropleth">
            <defs>
              <filter id="hoverShadow" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
                <feOffset dx="0" dy="1" result="offset" />
                <feMerge><feMergeNode in="offset" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {statesPaths.map((sp) => {
              const row = benLookup[sp.name];
              const isHov = hovered === sp.name;
              return (
                <path
                  key={sp.key}
                  d={sp.d}
                  data-testid={`geo-state-${sp.name.replace(/\s+/g, '-').toLowerCase()}`}
                  fill={colorFor(row?.beneficiaries || 0)}
                  stroke={isHov ? '#1B1B1B' : '#FFFFFF'}
                  strokeWidth={isHov ? 1.5 : 0.6}
                  vectorEffect="non-scaling-stroke"
                  onMouseEnter={() => onHover(sp.name)}
                  onMouseLeave={() => onHover(null)}
                  onFocus={() => onHover(sp.name)}
                  filter={isHov ? 'url(#hoverShadow)' : undefined}
                  style={{ cursor: row ? 'pointer' : 'default', transition: 'fill 150ms, stroke-width 120ms' }}
                >
                  <title>{sp.name}{row ? ` — ${row.beneficiaries.toLocaleString('en-IN')} beneficiaries` : ' — no live data'}</title>
                </path>
              );
            })}
          </svg>
        )}
      </div>

      <div className="mt-3 flex justify-between items-center flex-wrap gap-2 text-[11.5px] text-[color:var(--gray-500)]">
        <span>Boundaries: <b>India — 36 states &amp; UTs</b> · Mercator projection</span>
        <span>
          Cumulative reach: <b className="text-[color:var(--gray-800)] tabular-nums">{total.toLocaleString('en-IN')}</b> lives across {stateReach.length} states
        </span>
      </div>
    </div>
  );
}
